import React, { useEffect, useState } from 'react';
import Login from './pages/login/Login';
import Reset from "./pages/login/Reset";
import Inventory from './pages/inventory/Inventory';
import OrderStatus from './pages/order/OrderStatus';

import { Redirect, Route, Switch, useHistory } from "react-router-dom";

import './App.css';
import { Box, CircularProgress, Grid, Paper } from "@mui/material";
import AppDrawer from "./components/app/AppDrawer/AppDrawer";
import Cart from "./pages/cart/Cart";
import Logout from "./pages/logout/Logout";
import Home from "./pages/home/Home";
import { useGetTopLevelAccountsQuery, useGetUserInfoQuery, useGetOrgsQuery } from "./services/grace";
import logo from "./assets/grace_information-logo-red.svg";
import ProtectedRoute from "./components/app/ProtectedRoute/ProtectedRoute";
import { drawerWidth } from "./util/globals";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import AddressBook from "./pages/addressbook/AddressBook";
import Search from "./pages/search/Search";
import { AlertProvider, useAlertDispatch } from './context/AlertContext/AlertContext';
import { useAppDispatch, useAppSelector } from "./util/hooks";
import {
    setColumnDef as setInventoryColumnDef,
    setColumnVisibility as setInventoryColumnVisibility,
} from "./features/inventory/inventorySlice";
import {
    setColumnDef as setCartColumnDef,
} from "./features/cart/cartSlice";
import {
    setColumnDef as setOrderColumnDef,
} from "./features/order/orderSlice";
import { RootState } from "./util/store";
import { GRID_CHECKBOX_SELECTION_FIELD } from "@mui/x-data-grid";
import { convertAPIDef } from "./util/grid";
import { APIFormContainer, APIFormDef } from "./model/User";
import ProtectedAddDynamicFormRoute from "./components/app/ProtectedRoute/ProtectedAddDynamicFormRoute";
import ProtectedEditDynamicFormRoute from "./components/app/ProtectedRoute/ProtectedEditDynamicFormRoute";

function Main(): JSX.Element {
    const history = useHistory();
    const dispatch = useAppDispatch();
    const alertDispatch = useAlertDispatch();
    const columnVisibility = useAppSelector((state: RootState) => state.inventory.columnVisibility);

    // Catch user data load issues in this file
    const {
        data: userData,
        isError: userDataIsError,
        error: userDataError,
        isFetching: userDataIsFetching,
    } = useGetUserInfoQuery();

    // Catch top level account data load issues in this file
    const {
        isError: topLevelAccountDataIsError,
        isFetching: topLevelAccountDataIsFetching,
    } = useGetTopLevelAccountsQuery(null, { skip: userDataIsFetching || userDataIsError || !userData });

    // Catch org data load issues in this file
    const {
        isError: OrgsDataIsError,
        isFetching: OrgsDataIsFetching,
    } = useGetOrgsQuery(null, { skip: userDataIsFetching || userDataIsError || !userData });

    const [addContainerDef, setAddContainerDef] = useState<APIFormDef>({
        formId: "default",
        formDesc: "Default add carton form",
        itemType: "container",
        displayOrder: "H",
        columns: 2,
        fields: [],
    });

    const [editContainerDef, setEditContainerDef] = useState<APIFormDef>({
        formId: "default",
        formDesc: "Default edit carton form",
        itemType: "container",
        displayOrder: "H",
        columns: 2,
        fields: [],
    });

    const [addFileDef, setAddFileDef] = useState<APIFormDef>({
        formId: "default",
        formDesc: "Default add file form",
        itemType: "file",
        displayOrder: "H",
        columns: 2,
        fields: [],
    });

    const [editFileDef, setEditFileDef] = useState<APIFormDef>({
        formId: "default",
        formDesc: "Default edit file form",
        itemType: "file",
        displayOrder: "H",
        columns: 2,
        fields: [],
    });

    const [addTapeDef, setAddTapeDef] = useState<APIFormDef>({
        formId: "default",
        formDesc: "Default add tape form",
        itemType: "tape",
        displayOrder: "H",
        columns: 2,
        fields: [],
    });

    const [editTapeDef, setEditTapeDef] = useState<APIFormDef>({
        formId: "default",
        formDesc: "Default edit tape form",
        itemType: "tape",
        displayOrder: "H",
        columns: 2,
        fields: [],
    });

    const [userDataProcessed, setUserDataProcessed] = useState(false);

    useEffect(() => {
        if (!userDataIsFetching && userDataIsError) {
            const err = userDataError as FetchBaseQueryError;
            const pwdResetRegex = /^\/reset\/.+$/;

            if (err.status === 401 && history.location.pathname !== "/login" && !pwdResetRegex.test(history.location.pathname)) {
                // Use a redirect so that we refresh application state
                alertDispatch({ type: 'redirect' })
            }
        } else if (!userDataIsFetching && !userDataIsError) {
            dispatch(setInventoryColumnVisibility({
                ...columnVisibility,
                [GRID_CHECKBOX_SELECTION_FIELD]: true,
                cartStatus: userData === undefined || userData?.permissions.orderItems,
                id: false,
            }));

            if (userData !== undefined && userData.grids !== undefined) {
                // Process inventory grid definitions
                const inventoryDef = userData.grids.find((gridDef) => {
                    return gridDef.type === "inventory";
                })

                if (inventoryDef !== undefined) {
                    console.log("Loading inventory grid def");
                    try {
                        const newDef = convertAPIDef(
                            inventoryDef.grids[0],
                            [
                                // We always need an "id" field
                                {field: 'id'},
                                {field: 'view'},
                                {field: 'cartStatus'},
                                {field: 'scanId'},
                            ],
                        );
                        dispatch(setInventoryColumnDef(newDef));
                    } catch (e) {
                        console.error(e);
                    }
                }

                // Process cart grid definitions
                const cartDef = userData.grids.find((gridDef) => {
                    return gridDef.type === "cart";
                })

                if (cartDef !== undefined) {
                    console.log("Loading cart grid def");
                    try {
                        const newDef = convertAPIDef(
                            cartDef.grids[0],
                            [
                                {field: 'id'},
                                {field: 'remove'},
                            ],
                        );
                        dispatch(setCartColumnDef(newDef));
                    } catch (e) {
                        console.error(e);
                    }
                }

                // Process order grid definitions
                const orderDef = userData.grids.find((gridDef) => {
                    return gridDef.type === "orders";
                })

                if (orderDef !== undefined) {
                    console.log("Loading order grid def");
                    try {
                        const newDef = convertAPIDef(
                            orderDef.grids[0],
                            [
                                {field: 'id'},
                                {field: 'orderDetails'},
                            ],
                        );
                        dispatch(setOrderColumnDef(newDef));
                    } catch (e) {
                        console.error(e);
                    }
                }

                // Process add form definitions
                const addDef = userData?.forms.find((formContainerDef: APIFormContainer) => {
                    return formContainerDef.type === "add";
                })

                let currentDef = addDef?.forms.find((formDef: APIFormDef) => {
                    return formDef.itemType === "container";
                })

                if (currentDef !== undefined) {
                    setAddContainerDef(currentDef);
                }

                currentDef = addDef?.forms.find((formDef: APIFormDef) => {
                    return formDef.itemType === "file";
                })

                if (currentDef !== undefined) {
                    setAddFileDef(currentDef);
                }

                currentDef = addDef?.forms.find((formDef: APIFormDef) => {
                    return formDef.itemType === "tape";
                })

                if (currentDef !== undefined) {
                    setAddTapeDef(currentDef);
                }

                // Process edit form definitions
                const editDef = userData?.forms.find((formContainerDef: APIFormContainer) => {
                    return formContainerDef.type === "edit";
                })

                currentDef = editDef?.forms.find((formDef: APIFormDef) => {
                    return formDef.itemType === "container";
                })

                if (currentDef !== undefined) {
                    setEditContainerDef(currentDef);
                }

                currentDef = editDef?.forms.find((formDef: APIFormDef) => {
                    return formDef.itemType === "file";
                })

                if (currentDef !== undefined) {
                    setEditFileDef(currentDef);
                }

                currentDef = editDef?.forms.find((formDef: APIFormDef) => {
                    return formDef.itemType === "tape";
                })

                if (currentDef !== undefined) {
                    setEditTapeDef(currentDef);
                }
            }

            setUserDataProcessed(true);

            if (history.location.pathname === "/login") {
                // We're already logged in so just update the React route
                history.push("/");
            }
        }
    }, [dispatch, history, userDataIsFetching, userDataIsError, userDataError, alertDispatch])

    useEffect(() => {
        if (!topLevelAccountDataIsFetching && topLevelAccountDataIsError) {
            // We can't do more than show a snackbar for account errors
            // as 401 errors will already get caught in the user data query...
            // except we don't show them for "/" or "/login"
            if (!["/", "/login"].includes(history.location.pathname)) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        message: "Error retrieving top-level accounts",
                    }
                });
            }
        }
    }, [history, topLevelAccountDataIsFetching, topLevelAccountDataIsError, alertDispatch])

    return (
        <Switch>
            <Route path="/login" component={Login} />
            <Route path="/logout" component={Logout} />
            <Route path="/reset/:token" component={Reset} />
            <Route
                exact
                path={[
                    '/inventory/add/carton',
                    '/inventory/add/file',
                    '/inventory/add/tape',
                    '/inventory/edit/carton/:id',
                    '/inventory/edit/file/:id',
                    '/inventory/edit/tape/:id',
                    '/inventory/import',
                    '/search'
                ]}
            >
                {userDataIsFetching &&
                    <Grid
                        container
                        spacing={0}
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minHeight: '100vh' }}
                    >

                        <Grid item xs={3}>
                            <Paper sx={{
                                background: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: 2,
                                width: '400px'
                            }}>
                                <img src={logo} alt="Logo" width="100px"/>
                                <CircularProgress />
                            </Paper>
                        </Grid>
                    </Grid>
                }

                {(!userDataIsFetching && (userData === undefined || userDataIsError)) &&
                    <Redirect to='/login' />
                }

                {/*{(!userDataIsFetching && userData !== undefined && !userDataIsError) &&*/}
                {(!userDataIsFetching && userData !== undefined && !userDataIsError && userDataProcessed) &&
                    <div className="App">
                        <Box component={'main'} sx={{
                            flexGrow: 1,
                        }}>
                            <Switch>
                                <ProtectedAddDynamicFormRoute
                                    hasPermission={
                                        userData.permissions.addContainer
                                    }
                                    exact
                                    path="/inventory/add/carton"
                                    formDefinition={addContainerDef}
                                    formType="container"
                                />
                                <ProtectedAddDynamicFormRoute
                                    hasPermission={
                                        userData.permissions.addFilefolder
                                    }
                                    exact
                                    path="/inventory/add/file"
                                    formDefinition={addFileDef}
                                    formType="file"
                                />
                                <ProtectedAddDynamicFormRoute
                                    hasPermission={
                                        userData.permissions.addTape
                                    }
                                    exact
                                    path="/inventory/add/tape"
                                    formDefinition={addTapeDef}
                                    formType="tape"
                                />
                                <ProtectedEditDynamicFormRoute
                                    hasPermission={
                                        userData.permissions.search || userData.permissions.editItem
                                    }
                                    exact
                                    path="/inventory/edit/carton/:id"
                                    formDefinition={editContainerDef}
                                    formType="container"
                                />
                                <ProtectedEditDynamicFormRoute
                                    hasPermission={
                                        userData.permissions.search || userData.permissions.editItem
                                    }
                                    exact
                                    path="/inventory/edit/file/:id"
                                    formDefinition={editFileDef}
                                    formType="file"
                                />
                                <ProtectedEditDynamicFormRoute
                                    hasPermission={
                                        userData.permissions.search || userData.permissions.editItem
                                    }
                                    exact
                                    path="/inventory/edit/tape/:id"
                                    formDefinition={editTapeDef}
                                    formType="tape"
                                />
                                <ProtectedRoute
                                    hasPermission={
                                        userData.permissions.search
                                    }
                                    path="/search"
                                    component={Search}
                                />
                            </Switch>
                        </Box>
                    </div>
                }
            </Route>

            <Route exact path={['/', '/inventory', '/search', '/cart', '/order', '/addresses', '/report']}>
                {userDataIsFetching &&
                    <Grid
                        container
                        spacing={0}
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minHeight: '100vh' }}
                    >

                        <Grid item xs={3}>
                            <Paper sx={{
                                background: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: 2,
                                width: '400px'
                            }}>
                                <img src={logo} width="150px" style={{padding: '15px 0px 15px 0px'}} alt="Grace Logo" />
                                <CircularProgress />
                            </Paper>
                        </Grid>
                    </Grid>
                }

                {(!userDataIsFetching && (userData === undefined || userDataIsError)) &&
                    <Redirect to='/login' />
                }

                {(!userDataIsFetching && userData !== undefined && !userDataIsError) &&
                    <div className="App">
                        <Switch>
                            <Route exact path={['/inventory', '/cart', '/order', '/addresses', '/report']}>
                                <AppDrawer />
                            </Route>
                        </Switch>

                        <Switch>
                            <Route exact path="/" component={Home} />
                            <Route>
                                <Box component={'main'} sx={{
                                    flexGrow: 1,
                                    marginLeft: `${drawerWidth}px`,
                                    width: `calc(100% - ${drawerWidth}px)`,
                                }}>
                                    <Switch>
                                        <Route
                                            exact
                                            path="/"
                                            component={Home}
                                        />
                                        <ProtectedRoute
                                            hasPermission={
                                                userData.permissions.search
                                            }
                                            path="/inventory"
                                            component={Inventory}
                                        />
                                        <ProtectedRoute
                                            hasPermission={
                                                userData.permissions.orderItems ||
                                                userData.permissions.orderMaterials ||
                                                userData.permissions.orderServices
                                            }
                                            path="/cart"
                                            component={Cart}
                                        />
                                        <ProtectedRoute
                                            hasPermission={
                                                userData.permissions.orderStatus
                                            }
                                            path="/order"
                                            component={OrderStatus}
                                        />
                                        <ProtectedRoute
                                            hasPermission={
                                                userData.permissions.manageAddresses || true
                                            }
                                            path="/addresses"
                                            component={AddressBook}
                                        />
                                    </Switch>
                                </Box>
                            </Route>
                        </Switch>
                    </div>
                }
            </Route>
        </Switch>
    );
}

export default Main;
